/**
 * The single source of truth for Echo's role model, transcribed from
 * docs/RBAC_MODEL.md §2–§3.
 *
 * CLAUDE.md's workflow rule: "Every permission check goes through RBAC_MODEL.md's
 * roles, never an ad hoc role string invented in code." These unions are the
 * mechanism that enforces that — a typo'd or invented role name is a compile
 * error, in both `api` and `web`, because both import from here.
 *
 * Hierarchy is Platform → Organization → Brand (RBAC_MODEL.md §1). There is no
 * "agency" role: an agency is simply an Organization with many Brands.
 */

/** RBAC_MODEL.md §2, Organization scope. v1 has exactly one org-scope role. */
export const ORGANIZATION_ROLES = ["OrganizationAdmin"] as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

/** RBAC_MODEL.md §2, Brand scope. */
export const BRAND_ROLES = [
  "BrandAdmin",
  "CommsManager",
  "IRLead",
  "CrisisLead",
  "ExecutiveViewer",
] as const;
export type BrandRole = (typeof BRAND_ROLES)[number];

export type AnyRole = OrganizationRole | BrandRole;

export function isBrandRole(value: string): value is BrandRole {
  return (BRAND_ROLES as readonly string[]).includes(value);
}

export function isOrganizationRole(value: string): value is OrganizationRole {
  return (ORGANIZATION_ROLES as readonly string[]).includes(value);
}

/**
 * Capabilities are the rows of RBAC_MODEL.md §3's permission matrix.
 *
 * Capabilities for modules that don't exist yet (pillars, documents, claims,
 * prompts, engines, alerts, crisis mode, action items) are defined here
 * deliberately: Phase 0 ships the *whole* role model enforced by middleware
 * "even though most have nothing to act on yet"
 * (PHASED_IMPLEMENTATION_PLAN.md Phase 0). Later phases attach routes to these
 * rather than re-deriving the matrix.
 */
export const CAPABILITIES = [
  "org:manage_billing",
  "org:manage_brands",
  "org:manage_members",
  "org:manage_service_engagements",
  "org:view_audit_log",
  "brand:manage_members",
  "brand:manage_pillars",
  "brand:upload_documents",
  "brand:approve_claims",
  "brand:approve_prompts",
  "brand:configure_engines",
  "brand:configure_alerts",
  /**
   * FR-SCORE-03 — NDI severity bands and layer weights. Admin-only, and
   * deliberately NOT bundled into `brand:configure_alerts`: an alert threshold
   * is a notification preference, while this moves the headline number a board
   * sees. METRICS_SCORING_SPEC.md §9 treats a change here as a model change.
   */
  "brand:manage_scoring_config",
  "brand:toggle_crisis_mode",
  "brand:view_dashboard",
  "brand:manage_action_items",
  "brand:export_reports",
  "brand:view_audit_log",
  "platform:access_any_organization",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

/**
 * RBAC_MODEL.md §3, transcribed row by row for the five Brand-scoped roles.
 *
 * Note on the matrix's `👁` (read-only) cells: `brand:view_dashboard` and
 * `brand:export_reports` are themselves read actions, so ExecutiveViewer holds
 * them. `brand:view_audit_log` is likewise a read action, held by BrandAdmin
 * scoped to their own Brand. Anything mutating is simply absent from the set.
 */
const BRAND_ROLE_CAPABILITIES: Record<BrandRole, readonly Capability[]> = {
  BrandAdmin: [
    "brand:manage_members",
    "brand:manage_pillars",
    "brand:upload_documents",
    "brand:approve_claims",
    "brand:approve_prompts",
    "brand:configure_engines",
    "brand:configure_alerts",
    "brand:manage_scoring_config",
    "brand:toggle_crisis_mode",
    "brand:view_dashboard",
    "brand:manage_action_items",
    "brand:export_reports",
    "brand:view_audit_log",
  ],
  CommsManager: [
    "brand:upload_documents",
    "brand:approve_claims",
    "brand:approve_prompts",
    "brand:view_dashboard",
    "brand:manage_action_items",
    "brand:export_reports",
  ],
  // IRLead has CommsManager's permissions exactly. The IR distinction is a
  // dashboard default-filter concern, "not a hard permission wall"
  // (RBAC_MODEL.md §2) — so it must NOT diverge here.
  IRLead: [
    "brand:upload_documents",
    "brand:approve_claims",
    "brand:approve_prompts",
    "brand:view_dashboard",
    "brand:manage_action_items",
    "brand:export_reports",
  ],
  // CrisisLead is CommsManager + Crisis Mode. That single extra capability is
  // "the one functional role with a permission CommsManager doesn't have."
  CrisisLead: [
    "brand:upload_documents",
    "brand:approve_claims",
    "brand:approve_prompts",
    "brand:toggle_crisis_mode",
    "brand:view_dashboard",
    "brand:manage_action_items",
    "brand:export_reports",
  ],
  // Intentionally the tightest role: "if a screen has any doubt about whether
  // ExecutiveViewer should see an edit control, the answer is no."
  ExecutiveViewer: ["brand:view_dashboard", "brand:export_reports"],
};

/** RBAC_MODEL.md §3, OrgAdmin column — the org-scope rows only. */
const ORGANIZATION_ROLE_CAPABILITIES: Record<
  OrganizationRole,
  readonly Capability[]
> = {
  OrganizationAdmin: [
    "org:manage_billing",
    "org:manage_brands",
    "org:manage_members",
    "org:manage_service_engagements",
    "org:view_audit_log",
  ],
};

/**
 * OrganizationAdmin inherits BrandAdmin-equivalent access to every Brand under
 * their Organization, WITHOUT a BrandMembership row (RBAC_MODEL.md §4).
 *
 * DATA_MODEL.md §3 is explicit that this is an authorization-time rule:
 * "Do not write code that materializes a BrandMembership row per Brand for
 * OrgAdmins; check the inheritance rule at authorization time instead."
 */
export const ORG_ADMIN_INHERITED_BRAND_ROLE: BrandRole = "BrandAdmin";

export function capabilitiesForBrandRole(
  role: BrandRole,
): readonly Capability[] {
  return BRAND_ROLE_CAPABILITIES[role];
}

export function capabilitiesForOrganizationRole(
  role: OrganizationRole,
): readonly Capability[] {
  return ORGANIZATION_ROLE_CAPABILITIES[role];
}

export function brandRoleHasCapability(
  role: BrandRole,
  capability: Capability,
): boolean {
  return BRAND_ROLE_CAPABILITIES[role].includes(capability);
}

export function organizationRoleHasCapability(
  role: OrganizationRole,
  capability: Capability,
): boolean {
  return ORGANIZATION_ROLE_CAPABILITIES[role].includes(capability);
}

/** Human-readable labels for the UI. Keep in sync with RBAC_MODEL.md §2. */
export const ROLE_LABELS: Record<AnyRole, string> = {
  OrganizationAdmin: "Organization Admin",
  BrandAdmin: "Brand Admin",
  CommsManager: "Comms Manager",
  IRLead: "IR Lead",
  CrisisLead: "Crisis Lead",
  ExecutiveViewer: "Executive Viewer",
};

export const ROLE_DESCRIPTIONS: Record<AnyRole, string> = {
  OrganizationAdmin:
    "Manages billing, creates Brands, and inherits full access to every Brand in the Organization.",
  BrandAdmin:
    "Full control within this Brand, including inviting other users to it.",
  CommsManager:
    "Day-to-day operator — reviews drift, triages, and resolves action items.",
  IRLead:
    "Same permissions as Comms Manager, with the dashboard defaulted to financial and IR pillars.",
  CrisisLead:
    "Comms Manager permissions, plus the ability to toggle Crisis Mode.",
  ExecutiveViewer:
    "Read-only. Dashboard and report export only — the board-member login.",
};

/* ------------------------------------------------------------------------ *
 * Presentation layer for the matrix above.
 *
 * These exist so the "who can do what" reference the product SHOWS a customer
 * is derived from the same table the middleware ENFORCES, rather than being a
 * second, hand-written description of it that goes stale the first time a
 * capability moves. If a role gains a capability, the reference changes in the
 * same commit, because it is the same data.
 * ------------------------------------------------------------------------ */

/** Which level of the hierarchy a role is granted at (RBAC_MODEL.md §1). */
export const ROLE_SCOPE: Record<AnyRole, "organization" | "brand"> = {
  OrganizationAdmin: "organization",
  BrandAdmin: "brand",
  CommsManager: "brand",
  IRLead: "brand",
  CrisisLead: "brand",
  ExecutiveViewer: "brand",
};

/**
 * What an OrganizationAdmin can actually do, everywhere in their Organization.
 *
 * This is the union that answers the question people keep getting wrong: an
 * OrganizationAdmin is not "a BrandAdmin with billing" and not "an admin who
 * cannot touch Brands" — they hold the Organization capabilities AND, by
 * inheritance (RBAC_MODEL.md §4), BrandAdmin's full set on every Brand under
 * that Organization, including Brands created after they were granted.
 *
 * Note this is the *effective* set for display. Authorization still resolves
 * per Brand through resolveBrandAccess, so the inheritance is checked against
 * the specific Brand being accessed, never widened to "any Brand".
 */
export function effectiveCapabilitiesForRole(
  role: AnyRole,
): readonly Capability[] {
  if (role === "OrganizationAdmin") {
    return [
      ...ORGANIZATION_ROLE_CAPABILITIES.OrganizationAdmin,
      ...BRAND_ROLE_CAPABILITIES[ORG_ADMIN_INHERITED_BRAND_ROLE],
    ];
  }
  return BRAND_ROLE_CAPABILITIES[role];
}

export function roleHasCapability(
  role: AnyRole,
  capability: Capability,
): boolean {
  return effectiveCapabilitiesForRole(role).includes(capability);
}

/**
 * Plain-language names for each capability — what a comms lead would call it,
 * not what the code calls it. Ordered as the reference table renders them:
 * Organization-level rows first, then Brand-level, roughly following the
 * product's own workflow order (configure → ingest → approve → operate → read).
 */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  "org:manage_billing": "Manage billing and plan",
  "org:manage_brands": "Create and archive Brands",
  "org:manage_members": "Invite and remove Organization Admins",
  "org:manage_service_engagements": "Manage service engagements",
  "org:view_audit_log": "View the Organization audit log",
  "brand:manage_members": "Invite and remove people on a Brand",
  "brand:manage_pillars": "Create and edit Narrative Pillars",
  "brand:upload_documents": "Upload Ground Truth documents",
  "brand:approve_claims": "Approve or reject extracted claims",
  "brand:approve_prompts": "Approve or reject prompts",
  "brand:configure_engines": "Choose which AI engines are tracked",
  "brand:configure_alerts": "Configure alert rules and thresholds",
  "brand:manage_scoring_config": "Change NDI bands and scoring weights",
  "brand:toggle_crisis_mode": "Turn Crisis Mode on or off",
  "brand:view_dashboard": "View the dashboard and drift findings",
  "brand:manage_action_items": "Assign and resolve action items",
  "brand:export_reports": "Export board-ready reports",
  "brand:view_audit_log": "View this Brand’s audit log",
  "platform:access_any_organization": "Access any Organization for support",
};

/**
 * The rows of the customer-facing reference table, in display order.
 *
 * `platform:access_any_organization` is deliberately absent: it belongs to
 * Fulcro-internal SuperAdmins, and listing a row that every customer role
 * shows a dash against teaches nothing except that a role they will never meet
 * exists.
 */
export const CAPABILITY_MATRIX_ROWS: readonly Capability[] = [
  "org:manage_billing",
  "org:manage_brands",
  "org:manage_members",
  "brand:manage_members",
  "brand:manage_pillars",
  "brand:configure_engines",
  "brand:configure_alerts",
  "brand:manage_scoring_config",
  "brand:upload_documents",
  "brand:approve_claims",
  "brand:approve_prompts",
  "brand:toggle_crisis_mode",
  "brand:manage_action_items",
  "brand:view_dashboard",
  "brand:export_reports",
  "brand:view_audit_log",
];

/** One line on what each role is FOR — the job, not the permission list. */
export const ROLE_PURPOSE: Record<AnyRole, string> = {
  OrganizationAdmin:
    "The account owner. Holds billing, creates Brands, and automatically has full Brand Admin access to every Brand in the Organization — including ones created later.",
  BrandAdmin:
    "Runs one Brand end to end: its pillars, engines, thresholds, and who else can see it. No billing, and no access to other Brands.",
  CommsManager:
    "The daily operator. Uploads Ground Truth, approves claims and prompts, works the drift queue. Cannot change configuration or invite anyone.",
  IRLead:
    "A Comms Manager whose dashboard defaults to the financial and IR pillars. Identical permissions — the difference is what they see first, not what they may do.",
  CrisisLead:
    "A Comms Manager who can also turn Crisis Mode on, raising capture cadence and alert sensitivity during a live incident.",
  ExecutiveViewer:
    "The board login. Opens the dashboard, exports the PDF, changes nothing.",
};
